import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const STATUS_OPTIONS = [
  'Phone',
  'Email',
  'PAN done',
  'Other details done',
  'Address done',
  'Aadhar Done',
  'Payment done',
  'Nominee done',
  'VKYC done'
];

function LeadsList() {
  const { currentUser, logout, getLeads } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLead, setSelectedLead] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const token = localStorage.getItem('token');

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        setLoading(true);
        const response = await getLeads();
        setLeads(response.leads);
      } catch (error) {
        setError('Failed to fetch leads. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeads();
  }, []);

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      setUpdatingStatus(true);
      const response = await axios.patch(
        `${API_URL}/leads/${leadId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update leads list
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead._id === leadId ? response.data.lead : lead
        )
      );

      // Update selected lead if it's the one being modified
      if (selectedLead && selectedLead._id === leadId) {
        setSelectedLead(response.data.lead);
      }
    } catch (error) {
      alert('Failed to update status: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const addNote = async (leadId) => {
    if (!noteText.trim()) {
      alert('Please enter a note');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/leads/${leadId}/notes`,
        { note: noteText },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update leads list
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead._id === leadId ? response.data.lead : lead
        )
      );

      // Update selected lead
      if (selectedLead && selectedLead._id === leadId) {
        setSelectedLead(response.data.lead);
      }

      setNoteText('');
    } catch (error) {
      alert('Failed to add note: ' + (error.response?.data?.error || error.message));
    }
  };

  const getEmailStatusBadgeClass = (status) => {
    return status === 'sent' 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const getStatusBadgeClass = (status) => {
    const statusColors = {
      'Phone': 'bg-blue-100 text-blue-800',
      'Email': 'bg-indigo-100 text-indigo-800',
      'PAN done': 'bg-purple-100 text-purple-800',
      'Other details done': 'bg-pink-100 text-pink-800',
      'Address done': 'bg-orange-100 text-orange-800',
      'Aadhar Done': 'bg-yellow-100 text-yellow-800',
      'Payment done': 'bg-teal-100 text-teal-800',
      'Nominee done': 'bg-cyan-100 text-cyan-800',
      'VKYC done': 'bg-green-100 text-green-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">42Cards Solutions DSA Portal</h1>
          <div className="flex items-center">
            <span className="mr-4 text-gray-600">Welcome, {currentUser?.name}</span>
            <button
              onClick={handleLogout}
              className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition duration-200"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center">
              <div className="flex space-x-4">
                <Link
                  to="/dashboard"
                  className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  Dashboard
                </Link>
                <Link
                  to="/generate-lead"
                  className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  Generate Lead
                </Link>
                <Link
                  to="/leads"
                  className="text-white px-3 py-2 rounded-md text-sm font-medium bg-gray-900"
                >
                  My Leads
                </Link>
                <Link
                  to="/help"
                  className="text-gray-300 hover:bg-gray-700 hover:text-white px-3 py-2 rounded-md text-sm font-medium"
                >
                  Help
                </Link>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main>
        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">My Leads</h2>
                <Link
                  to="/generate-lead"
                  className="bg-blue-600 text-white px-4 py-1 rounded hover:bg-blue-700 transition duration-200"
                >
                  + New Lead
                </Link>
              </div>
              
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}
              
              {loading ? (
                <div className="text-center py-10">
                  <p className="text-gray-500">Loading leads...</p>
                </div>
              ) : leads.length === 0 ? (
                <div className="text-center py-10 border border-gray-200 rounded-lg">
                  <p className="text-gray-500">No leads found.</p>
                  <Link
                    to="/generate-lead"
                    className="inline-block mt-3 text-blue-600 hover:underline"
                  >
                    Generate your first lead
                  </Link>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Lead ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {leads.map((lead) => (
                        <tr key={lead._id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                            {lead.referralCode}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{lead.customerName || 'N/A'}</div>
                            <div className="text-sm text-gray-500">{lead.customerPhone}</div>
                            <div className="text-sm text-gray-500">{lead.customerEmail}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                            {lead.product}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-800">
                            {new Date(lead.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(lead.status || 'Phone')}`}>
                              {lead.status || 'Phone'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full border ${getEmailStatusBadgeClass(lead.emailStatus)}`}>
                              {lead.emailStatus}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(lead.uniqueLink);
                                alert('Link copied to clipboard!');
                              }}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              Copy Link
                            </button>
                            <button
                              onClick={() => setSelectedLead(lead)}
                              className="text-green-600 hover:text-green-800"
                            >
                              View / Edit
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Lead Details Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold">Lead Details</h3>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Lead ID</p>
                  <p className="font-medium font-mono text-blue-600">{selectedLead.referralCode}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Customer Name</p>
                  <p className="font-medium">{selectedLead.customerName || 'N/A'}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium">{selectedLead.customerPhone}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{selectedLead.customerEmail}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Product</p>
                  <p className="font-medium">{selectedLead.product}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Application Link</p>
                  <p className="font-mono text-sm break-all text-blue-600">{selectedLead.uniqueLink}</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedLead.uniqueLink);
                      alert('Link copied!');
                    }}
                    className="mt-2 text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Copy Link
                  </button>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">Status</p>
                  <select
                    value={selectedLead.status || 'Phone'}
                    onChange={(e) => updateLeadStatus(selectedLead._id, e.target.value)}
                    disabled={updatingStatus}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Email Status</p>
                  <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full border ${getEmailStatusBadgeClass(selectedLead.emailStatus)}`}>
                    {selectedLead.emailStatus}
                  </span>
                </div>

                <div>
                  <p className="text-sm text-gray-600 mb-2">Notes</p>
                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                    {selectedLead.notes && selectedLead.notes.length > 0 ? (
                      selectedLead.notes.map(note => (
                        <div key={note._id} className="bg-gray-50 p-3 rounded border">
                          <p className="text-sm">{note.text}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            By {note.addedBy} on {new Date(note.createdAt).toLocaleString()}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No notes yet</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addNote(selectedLead._id);
                        }
                      }}
                    />
                    <button
                      onClick={() => addNote(selectedLead._id)}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      Add Note
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Created At</p>
                  <p className="font-medium">{new Date(selectedLead.createdAt).toLocaleString()}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Last Updated</p>
                  <p className="font-medium">{new Date(selectedLead.updatedAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadsList;